import type React from 'react';
import { useEffect, useMemo } from 'react';
import { Box, Icon, Tag, Text, Tooltip } from '@chakra-ui/react';
import { UserOutlined } from '@ant-design/icons';
import useMeetingParticipants from '@/features/meeting/hooks/useMeetingParticipantsList';
import type { Meeting, User } from '@/services/API';
import { LiveKitVideo } from './LiveKitVideo';

interface MeetingPiPProps {
  meeting: Meeting | null;
  user: User | null | undefined;
  userType: 'ADMIN' | 'HOST' | 'GUEST';
}

/**
 * Meeting Picture-in-Picture component that handles both LiveKit and legacy streams
 * This component replaces the external PipStreamVideo component
 */
export const MeetingPiP: React.FC<MeetingPiPProps> = ({ meeting, user, userType }) => {
  const participants = useMeetingParticipants(meeting?.id);
  // Determine mode based on user type: Only HOST = stream, others = join
  const isStreamer = userType === 'HOST';
  const isVisible = meeting?.isStreamingHost;

  // Generate unique participant name using real user data to avoid conflicts
  const participantName = useMemo(() => {
    if (!user?.id || !meeting?.id) {
      // Fallback for missing user/meeting data
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      return `anonymous-${timestamp}-${random}`;
    }

    // Use real user firstname and ID for unique, human-readable participant names
    // Format: "firstname-userID-mode-timestamp" (e.g., "John-abc123xyz-stream-1234567890")
    const safeName = user.firstname.replace(/[^a-zA-Z0-9]/g, ''); // Remove special chars
    const timestamp = Date.now();
    const userMode = isStreamer ? 'stream' : 'join';
    return `${safeName}-${user.id}-${userMode}-${timestamp}`;
  }, [user?.id, user?.firstname, meeting?.id, isStreamer]); // More stable dependencies

  // Filter to get only temporary users (remote viewers)
  const remoteViewers = useMemo(() => {
    return participants.filter((user) => user.isTemporary);
  }, [participants]);

  // Use LiveKit video streaming
  const mode = isStreamer ? 'stream' : 'join';

  // Debug logs (only on significant changes)
  useEffect(() => {
    console.log('🔍 MeetingPiP Debug:', {
      userType,
      mode,
      meetingId: meeting?.id,
      isVisible,
    });
  }, [userType, mode, meeting?.id, isVisible]);

  // --------------------- RENDER ------------------------

  if (!isVisible) {
    return null;
  }

  return (
    <Box position="relative">
      <LiveKitVideo
        roomName={`meeting-${meeting.id}`}
        participantName={participantName}
        mode={mode}
      />

      {
        // Notification bullet to display the number of remote viewers
        // Only show remote viewers for streamers
        isStreamer && (
          <Tooltip
            borderRadius="md"
            label={
              <Box p={1}>
                <Text fontWeight="bold" mb={1}>
                  {remoteViewers.length} remote viewer(s):
                </Text>
                {remoteViewers.map((viewer) => (
                  <Text key={viewer.id} fontSize="sm">
                    - {viewer.firstname}
                  </Text>
                ))}
              </Box>
            }
            placement="left"
            hasArrow
          >
            <Box zIndex={1100} position="absolute" top="20px" right="5px">
              <Tag color="green.800" bgColor="green.300" fontWeight="bold" cursor="pointer">
                <Icon as={UserOutlined} /> {remoteViewers.length}
              </Tag>
            </Box>
          </Tooltip>
        )
      }
    </Box>
  );
};
